import { passwordSchema } from '@nebueye/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { MailCheck } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { z } from 'zod'
import { DiscordIcon, Field, FormAlert } from '@/components/app/form'
import { CaptchaSubmit, useCaptcha } from '@/components/app/turnstile'
import { Button } from '@/components/ui/button'
import { authClient, authErrorMessage } from '@/lib/auth-client'
import { configQuery } from '@/lib/queries'
import { DiscordButton, OrDivider } from './-auth-shared'

export const Route = createFileRoute('/_auth/sign-up')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  head: () => ({ meta: [{ title: 'Create your account · Nebueye' }] }),
  component: SignUp,
})

const schema = z.object({
  name: z.string().trim().min(1, 'Tell us what to call you').max(80),
  email: z.email('Enter a valid email address'),
  password: passwordSchema,
})

function SignUp() {
  const search = Route.useSearch()
  const [errors, setErrors] = useState<Partial<Record<keyof z.infer<typeof schema>, string>>>({})
  const [formError, setFormError] = useState<string>()
  const [pending, setPending] = useState(false)
  const [sentTo, setSentTo] = useState<string>()
  const config = useQuery(configQuery())
  const inviteOnly = config.data?.signups === 'invite'
  // Invited people arrive here from their invitation link; the server checks the email matches it.
  const invited = search.redirect?.startsWith('/invite/') ?? false
  const captcha = useCaptcha('sign-up')

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const parsed = schema.safeParse(Object.fromEntries(new FormData(e.currentTarget)))
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path[0], i.message])))
      return
    }
    setErrors({})
    setFormError(undefined)
    setPending(true)
    const { error } = await authClient.signUp.email({
      ...parsed.data,
      // After the email link: onboarding (or straight to the invite they came from).
      callbackURL: search.redirect?.startsWith('/invite/') ? search.redirect : '/onboarding',
      fetchOptions: { headers: captcha.headers },
    })
    captcha.reset()
    setPending(false)
    if (error) return setFormError(authErrorMessage(error))
    setSentTo(parsed.data.email)
  }

  if (sentTo) return <CheckInbox email={sentTo} />

  if (inviteOnly && !invited) {
    return (
      <div className="space-y-7">
        <div className="space-y-2">
          <h1 className="text-headline font-semibold">Invite-only for now</h1>
          <p className="text-muted-foreground text-[15px]">
            Nebueye is opening up a few Vaults at a time. If someone invited you, open the link in that email to create your account.
          </p>
        </div>
        <p className="text-muted-foreground text-sm">
          Already have an account?{' '}
          <Link to="/sign-in" search={{ redirect: search.redirect }} className="text-primary font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-7">
      <div className="space-y-2">
        <h1 className="text-headline font-semibold">Create your account</h1>
        <p className="text-muted-foreground text-[15px]">
          {invited ? 'Use the email address your invitation was sent to.' : 'Track every drop, order and delivery in one place.'}
        </p>
      </div>

      {!inviteOnly && (
        <>
          <DiscordButton callbackURL="/" label="Sign up with Discord" icon={<DiscordIcon className="size-4" />} />
          <OrDivider />
        </>
      )}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {formError && <FormAlert>{formError}</FormAlert>}
        <Field label="Name" name="name" autoComplete="name" error={errors.name} autoFocus />
        <Field label="Email" name="email" type="email" autoComplete="email" error={errors.email} />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          error={errors.password}
          hint="At least 10 characters."
        />
        {captcha.widget}
        <CaptchaSubmit captcha={captcha} pending={pending}>
          Create account
        </CaptchaSubmit>
      </form>

      <p className="text-muted-foreground text-sm">
        Already have an account?{' '}
        <Link to="/sign-in" search={{ redirect: search.redirect }} className="text-primary font-medium hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  )
}

function CheckInbox({ email }: { email: string }) {
  const [resent, setResent] = useState(false)
  return (
    <div className="space-y-6">
      <span className="bg-accent text-accent-foreground grid size-12 place-items-center rounded-xl">
        <MailCheck className="size-6" />
      </span>
      <div className="space-y-2">
        <h1 className="text-headline font-semibold">Check your inbox</h1>
        <p className="text-muted-foreground text-[15px] leading-6">
          We sent a confirmation link to <span className="text-foreground font-medium">{email}</span>. Open it on this
          device to finish setting up. The link expires in 1 hour.
        </p>
      </div>
      <Button
        variant="outline"
        disabled={resent}
        onClick={async () => {
          await authClient.sendVerificationEmail({ email, callbackURL: '/onboarding' })
          setResent(true)
        }}
      >
        {resent ? 'Sent — check spam too' : 'Resend link'}
      </Button>
    </div>
  )
}
